/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import axios from "axios";
import configurations from "config";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "ORDERCONTEXT": {
      return { ...state, orderContext: action.value };
    }

    case "WITHDRAWALCONTEXT": {
      return { ...state, withdrawalContext: action.value };
    }

    case "TICKETCONTEXT": {
      return { ...state, ticketContext: action.value };
    }

    case "RETURNREQUESTCONTEXT": {
      return { ...state, returnRequestContext: action.value };
    }

    case "PRODUCTCONTEXT":{
      return {...state,productContext: action.value };
    }

    case "SERVICECONTEXT":{
      return { ...state,serviceContext: action.value };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children, settings }) {

  const [initialState, setInitialState] = useState({
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,

    locale: settings.locale,
    paginationSize:settings.paginationSize,
    MyContext:settings,
    orderContext: {
      orderBy: 'orderid',
      orderByType: 'desc',
      filterBy: 'none',
      searchBy: 'none',
      paymentStatus: 'all',
      orderStatus: 'all',
      timePeriod: 'all',
      searchByValue: '',
      fulfillmentModelOpen: false,
      unfulfilledTableData: {},
      fulfilledTableData: {},
      orderDetails: {},
    },
    withdrawalContext:{
      statusFilter: 'all',
      searchBy: 'none',
      searchValue: '',
      orderBy: 'id',
      orderValue: 'DESC',
    },
    ticketContext:{
      status: 'all'
    },

    returnRequestContext:{
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: '',
      returnDetails: {},
    },

    productContext:{
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: '',
    },

    serviceContext:{
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: '',
    },

  });

  const baseUrl = configurations.baseUrl;
  const azureFunctionVendorFrontendKey = configurations.azureFunctionVendorFrontendKey;
  const [controller, dispatch] = useReducer(reducer, initialState);



  const value = useMemo(() => [controller, dispatch,initialState], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setOrderContext = (dispatch, value) => dispatch({ type: "ORDERCONTEXT", value });
const setWithdrawalContext = (dispatch, value) => dispatch({ type: "WITHDRAWALCONTEXT", value });
const setReturnRequestContext=(dispatch,value)=>dispatch({type:"RETURNREQUESTCONTEXT",value});
const setTicketContext = (dispatch, value) => dispatch({ type: "TICKETCONTEXT", value });
const setProductContext = (dispatch, value) => dispatch({ type : "PRODUCTCONTEXT", value});
const setServiceContext = (dispatch, value) => dispatch({ type : "SERVICECONTEXT" , value});

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setOrderContext,
  setWithdrawalContext,
  setTicketContext,
  setReturnRequestContext,
  setProductContext,
  setServiceContext

};
